import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Send, HelpCircle, Target, Trophy, X, ZoomIn } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api, getSessionId } from '../config/api';
import './GuessWhatGame.css';

function GuessWhatGame() {
  const { gameId } = useParams();
  const { user } = useAuth();
  
  const [game, setGame] = useState(null);
  const [myPicture, setMyPicture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [eliminatedImages, setEliminatedImages] = useState(new Set());
  const [question, setQuestion] = useState('');
  const [isGuessing, setIsGuessing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  
  // Cache image URLs to prevent constant re-fetching
  const imageUrlCache = useRef(new Map());
  const gameLogRef = useRef(null);

  const fetchGame = useCallback(async () => {
    try {
      const data = await api.getGameState(gameId);
      
      // Cache image URLs - only update if we don't have them yet
      if (data.game?.images) {
        data.game.images = data.game.images.map(img => {
          if (!imageUrlCache.current.has(img.id)) {
            imageUrlCache.current.set(img.id, img.url);
          }
          return { ...img, url: imageUrlCache.current.get(img.id) };
        });
      }
      
      // Same for myPicture
      if (data.myThing && !imageUrlCache.current.has(data.myThing.id)) {
        imageUrlCache.current.set(data.myThing.id, data.myThing.url);
      }
      if (data.myThing) {
        data.myThing = { ...data.myThing, url: imageUrlCache.current.get(data.myThing.id) };
      }
      
      setGame(data.game);
      setMyPicture(data.myThing);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => {
    fetchGame();
    const interval = setInterval(fetchGame, 2000);
    return () => clearInterval(interval);
  }, [fetchGame]);

  const toggleEliminated = (imageId) => {
    if (isGuessing) return;
    
    setEliminatedImages(prev => {
      const next = new Set(prev);
      if (next.has(imageId)) {
        next.delete(imageId);
      } else {
        next.add(imageId);
      }
      return next;
    });
  };

  // Auto-scroll chat to bottom when moves change
  useEffect(() => {
    if (gameLogRef.current) {
      gameLogRef.current.scrollTop = gameLogRef.current.scrollHeight;
    }
  }, [game?.moves?.length]);

  // Find MY last question and its answer - only show if it's the most recent Q&A exchange
  const getEffectiveUid = () => user?.uid || `anon_${getSessionId()}`;
  const getMyLastQuestionInfo = () => {
    if (!game?.moves || game.moves.length === 0) return null;
    
    const moves = game.moves;
    const lastMove = moves[moves.length - 1];
    const secondLastMove = moves.length >= 2 ? moves[moves.length - 2] : null;
    
    // Case 1: I just asked a question (pending answer)
    if (lastMove.type === 'ask' && lastMove.playerUid === getEffectiveUid()) {
      return { question: lastMove.question, answered: false };
    }
    
    // Case 2: My question was just answered (show the Q&A)
    if (lastMove.type === 'answer' && secondLastMove?.type === 'ask' && secondLastMove.playerUid === getEffectiveUid()) {
      return { question: secondLastMove.question, answer: lastMove.answer, answered: true };
    }
    
    // Otherwise, don't show old Q&A - opponent has moved on
    return null;
  };
  const myLastQuestion = getMyLastQuestionInfo();

  const handleAskQuestion = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    
    setSubmitting(true);
    setError(null);
    try {
      await api.submitMove(gameId, {
        type: 'ask',
        question: question.trim()
      });
      setQuestion('');
      await fetchGame();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnswer = async (answer) => {
    setSubmitting(true);
    setError(null);
    try {
      await api.submitMove(gameId, {
        type: 'answer',
        answer: answer
      });
      await fetchGame();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGuess = async (imageId) => {
    setSubmitting(true);
    setError(null);
    try {
      await api.submitMove(gameId, {
        type: 'guess',
        imageId: imageId
      });
      setIsGuessing(false);
      await fetchGame();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="guesswhat-page">
        <div className="container">
          <div className="loading">Loading game...</div>
        </div>
      </div>
    );
  }

  if (error && !game) {
    return (
      <div className="guesswhat-page">
        <div className="container">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  // Get effective UID - either from auth or from session ID for anonymous players
  const effectiveUid = user?.uid || `anon_${getSessionId()}`;
  
  const isMyTurn = game?.currentTurnUid === effectiveUid;
  const needToAnswer = game?.pendingQuestion && game?.currentTurnUid === effectiveUid;
  const opponent = game?.players?.find(p => p.uid !== effectiveUid);
  const isFinished = game?.status === 'finished';
  const isWinner = game?.winnerUid === effectiveUid;

  return (
    <div className="guesswhat-page">
      <div className="container">
        {isFinished && (
          <div className={`game-over-banner ${isWinner ? 'winner' : 'loser'}`}>
            <Trophy size={32} />
            <span>{isWinner ? 'You Win!' : `${opponent?.displayName || 'Opponent'} Wins!`}</span>
          </div>
        )}

        <div className="game-header">
          <div className="game-info">
            <h1>Guess What</h1>
          </div>
          
          {myPicture && (
            <div className="my-picture">
              <span className="my-picture-label">Your Picture:</span>
              <div className="my-picture-wrapper">
                <img 
                  src={myPicture.url} 
                  alt="Your selected item" 
                  className="my-picture-image"
                />
                <button 
                  className="zoom-button"
                  onClick={(e) => { e.stopPropagation(); setZoomedImage(myPicture.url); }}
                >
                  <ZoomIn size={12} />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className={`image-grid ${isGuessing ? 'guessing-mode' : ''}`}>
          {game?.images?.map((image) => (
            <div
              key={image.id}
              className={`image-cell ${eliminatedImages.has(image.id) ? 'eliminated' : ''} ${image.id === myPicture?.id ? 'my-picture-highlight' : ''}`}
              onClick={() => isGuessing ? handleGuess(image.id) : toggleEliminated(image.id)}
            >
              <img src={image.url} alt="" loading="lazy" />
              {image.id === myPicture?.id && <div className="my-picture-marker">YOURS</div>}
              <button 
                className="zoom-button"
                onClick={(e) => { e.stopPropagation(); setZoomedImage(image.url); }}
              >
                <ZoomIn size={14} />
              </button>
            </div>
          ))}
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="status-section">
          {isFinished ? (
            <p className="status-text game-over">Game Over</p>
          ) : needToAnswer && game.pendingQuestion ? (
            <>
              <p className="status-text">Answer the question!</p>
              <p className="pending-question">
                <HelpCircle size={20} />
                "{game.pendingQuestion}"
              </p>
              <div className="answer-buttons">
                <button 
                  onClick={() => handleAnswer(true)} 
                  disabled={submitting}
                  className="answer-button yes"
                >
                  Yes
                </button>
                <button 
                  onClick={() => handleAnswer(false)} 
                  disabled={submitting}
                  className="answer-button no"
                >
                  No
                </button>
              </div>
            </>
          ) : isMyTurn ? (
            <>
              <p className="status-text">Your turn</p>
              {myLastQuestion?.answered && (
                <p className="last-answer">
                  Your question: "{myLastQuestion.question}" — <strong className={myLastQuestion.answer ? 'answer-yes' : 'answer-no'}>{myLastQuestion.answer ? 'Yes' : 'No'}</strong>
                </p>
              )}
            </>
          ) : (
            <>
              <p className="status-text waiting">Waiting for {opponent?.displayName || 'opponent'}...</p>
              {myLastQuestion && (
                <p className="last-answer">
                  Your question: "{myLastQuestion.question}"
                  {myLastQuestion.answered && <> — <strong className={myLastQuestion.answer ? 'answer-yes' : 'answer-no'}>{myLastQuestion.answer ? 'Yes' : 'No'}</strong></>}
                </p>
              )}
            </>
          )}
        </div>

        {!isFinished && (
          <div className={`action-section ${(!isMyTurn || needToAnswer) ? 'disabled' : ''}`}>
            {isGuessing ? (
              <div className="guessing-prompt">
                <p>Click an image to guess your opponent's picture</p>
                <button onClick={() => setIsGuessing(false)} className="cancel-guess">
                  <X size={18} /> Cancel
                </button>
              </div>
            ) : (
              <>
                <form onSubmit={handleAskQuestion} className="question-form">
                  <input
                    type="text"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="Ask a yes/no question..."
                    disabled={submitting || !isMyTurn || needToAnswer}
                    className="question-input"
                  />
                  <button type="submit" disabled={submitting || !question.trim() || !isMyTurn || needToAnswer} className="ask-button">
                    <Send size={18} />
                  </button>
                </form>
                <button onClick={() => setIsGuessing(true)} disabled={!isMyTurn || needToAnswer} className="guess-button">
                  <Target size={18} />
                  Make a Guess
                </button>
              </>
            )}
          </div>
        )}

        
        <div className="game-log" ref={gameLogRef}>
          {game?.moves?.map((move, index) => (
            <div key={index} className={`log-entry log-${move.type}`}>
              {move.type === 'ask' && (
                <>
                  <strong>{move.playerName}:</strong> {move.question}
                </>
              )}
              {move.type === 'answer' && (
                <>
                  <strong>{move.playerName}:</strong> {move.answer ? 'Yes' : 'No'}
                </>
              )}
              {move.type === 'guess' && (
                <div className="guess-log-entry">
                  <strong>{move.playerName}</strong> guessed 
                  {move.imageId && (
                    <img 
                      src={imageUrlCache.current.get(move.imageId) || game?.images?.find(img => img.id === move.imageId)?.url} 
                      alt="guessed" 
                      className="guess-thumbnail"
                    />
                  )}
                  {move.correct ? ' correctly!' : ' incorrectly.'}
                </div>
              )}
            </div>
          ))}
        </div>

        {zoomedImage && (
          <div className="zoom-overlay" onClick={() => setZoomedImage(null)}>
            <img src={zoomedImage} alt="Zoomed view" />
          </div>
        )}
      </div>
    </div>
  );
}

export default GuessWhatGame;
